/**
 * Spawn - actor and component instantiation utilities with prefab and type support.
 */
import { Actor } from '../actors/Actor.js';
import { SceneComponent } from '../components/SceneComponent.js';
import { AssetPath } from '../storage/storageProvider.js';
import { ClassRegistry, type Constructor } from '../systems/ClassRegistry.js';

import { instantiatePrefab, instantiatePrefabAsync } from './serialization/prefab.js';

import type { SerializedData } from './index.js';
import type { InstantiationOptions } from './serialization/prefab.js';


export type SpawnableType = string | AssetPath | Constructor;

/**
 * A typed wrapper that represents a spawnable value with a compile-time type constraint on the spawned result.
 * Designed for use with the `@property()` decorator so that the editor can infer the expected spawn type.
 *
 * @template T - The type of Actor that will be produced when this value is spawned.
 */
export type Spawnable<T extends Actor> = (string | AssetPath | Constructor<T>) & { readonly __spawnType?: T };

/**
 * Configuration options for spawning instances.
 */
export interface SpawnOptions extends InstantiationOptions {
  prefabData?: Record<string, SerializedData>;
}

type InitOptions<T> = T extends { initialize(options: infer O): any } ? O : never;
type InitParams<T> = T extends { initialize: (...args: infer A) => any } ? A : never;
type NonConstructorArg<T> = [T] extends [Constructor] ? never : T;

/**
 * Checks if the initialize method has a parameter that includes undefined (is optional).
 */
type IsOptionalInitialize<T> = InitOptions<T> extends never
  ? true  // No initialize method or no options parameter
  : undefined extends InitOptions<T>
    ? true  // Options type includes undefined
    : false; // Required options

/**
 * Extracts the spawn arguments type for a given Actor type.
 * If initialize() has required options, options parameter is required.
 * If initialize() has optional/no options, options parameter is optional.
 */
type SpawnArgs<T> = IsOptionalInitialize<T> extends true
  ? [options?: (InitOptions<T> & SpawnOptions) | undefined]
  : [options: InitOptions<T> & SpawnOptions];

function extractClsOrPrefabPath(prefabOrClass: SpawnableType): { cls: Constructor | null; prefabPath: string | AssetPath | null } {
  let cls: Constructor | null = null;
  let prefabPath: string | AssetPath | null = null;

  if (typeof prefabOrClass === 'function' && prefabOrClass.prototype) {
    // it's a class constructor
    cls = prefabOrClass;
  } else if (prefabOrClass instanceof AssetPath) {
    prefabPath = prefabOrClass;
  } else if (typeof prefabOrClass === 'string') {
    // it's a string path
    try {
      cls = ClassRegistry.getClass(prefabOrClass);
    }
    catch {
      // ignore
    }
    if (!cls) {
      // not a registered class, treat as prefab path
      prefabPath = prefabOrClass;
    }
  }
  return { cls, prefabPath };
}

function spawnByClass<T = Actor>(cls: Constructor<T>, options?: SpawnOptions): T {
  // Note: cls.prototype instanceof X is false when cls IS X (base class),
  // so we need to check cls === X as well. The cast is required because
  // TypeScript considers Constructor<T> and typeof Actor structurally incompatible.
  const clsCtor = cls as unknown as typeof Actor | typeof SceneComponent;
  const hasCreate = clsCtor === Actor || cls.prototype instanceof Actor
    || clsCtor === SceneComponent || cls.prototype instanceof SceneComponent;

  // a bit ugly, but essentially it says, if cls is an Actor or SceneComponent, use the create method, otherwise use the new operator
  const spawned = hasCreate ? (cls as unknown as { create(options: any): T }).create(options) : new cls();
  return spawned;
}

/**
 * Synchronously spawns an instance from a class constructor.
 * The return type is automatically inferred from the constructor.
 *
 * @param prefabOrClass - A class constructor (e.g., `MyActor`)
 * @param options - Optional spawn configuration including constructor arguments and prefab data overrides
 * @returns The spawned instance (type inferred from constructor)
 */
export function spawn<T extends Actor, C extends Constructor<Actor> = Constructor<Actor>>(prefabOrClass: C, ...args: SpawnArgs<InstanceType<C>>): InstanceType<C>;

/**
 * Synchronously spawns an instance from a `Spawnable<T>` value.
 * The return type T is automatically inferred from the branded type.
 *
 * @param prefabOrClass - A `Spawnable<T>` value carrying type information
 * @param options - Optional spawn configuration including constructor arguments and prefab data overrides
 * @returns The spawned instance of type T
 */
export function spawn<T extends Actor, P extends Spawnable<T>>(prefabOrClass: Spawnable<T> & NonConstructorArg<P>, ...args: SpawnArgs<T>): NoInfer<T>;

/**
 * Synchronously spawns an instance from a string path or AssetPath.
 * The type parameter T MUST be explicitly specified since it cannot be inferred.
 *
 * @template T - The expected return type of the spawned instance (MUST be explicitly specified)
 * @param prefabOrClass - A registered class name string, or prefab AssetPath/string
 * @param options - Optional spawn configuration including constructor arguments and prefab data overrides
 * @returns The spawned instance of type T
 */
export function spawn<T extends Actor = never>(prefabOrClass: string | AssetPath, ...args: SpawnArgs<T>): T;

/**
 * Implementation signature that handles all spawn cases.
 * @internal
 */
export function spawn<T extends Actor>(prefabOrClass: SpawnableType, ...args: [options?: SpawnOptions]): T {
  const [options] = args;
  const { cls, prefabPath } = extractClsOrPrefabPath(prefabOrClass);

  if (cls) {
    return spawnByClass<T>(cls, options);
  } else if (prefabPath) {
    const { prefabData, ...instOptions } = options ?? {};
    return instantiatePrefab<T>(prefabPath, prefabData, instOptions);
  }

  throw new Error(`Cannot spawn from ${prefabOrClass}`);
}

/**
 * Asynchronously spawns an instance from a class constructor.
 * The return type is automatically inferred from the constructor.
 *
 * @param prefabOrClass - A class constructor (e.g., `MyActor`)
 * @param options - Optional spawn configuration including constructor arguments and prefab data overrides
 * @returns A promise resolving to the spawned instance (type inferred from constructor)
 */
export function spawnAsync<T extends Actor, C extends Constructor<Actor> = Constructor<Actor>>(prefabOrClass: C, ...args: SpawnArgs<InstanceType<C>>): Promise<InstanceType<C>>;

/**
 * Asynchronously spawns an instance from a `Spawnable<T>` value.
 * The return type T is automatically inferred from the branded type.
 *
 * @param prefabOrClass - A `Spawnable<T>` value carrying type information
 * @param options - Optional spawn configuration including constructor arguments and prefab data overrides
 * @returns A promise resolving to the spawned instance of type T
 */
export function spawnAsync<T extends Actor, P extends Spawnable<T>>(prefabOrClass: Spawnable<T> & NonConstructorArg<P>, ...args: SpawnArgs<T>): Promise<NoInfer<T>>;

/**
 * Asynchronously spawns an instance from a string path or AssetPath.
 * The type parameter T MUST be explicitly specified since it cannot be inferred.
 *
 * @template T - The expected return type of the spawned instance (MUST be explicitly specified)
 * @param prefabOrClass - A registered class name string, or prefab AssetPath/string
 * @param options - Optional spawn configuration including constructor arguments and prefab data overrides
 * @returns A promise resolving to the spawned instance of type T
 */
export function spawnAsync<T extends Actor = never>(prefabOrClass: string | AssetPath, ...args: SpawnArgs<T>): Promise<T>;

/**
 * Implementation signature that handles all async spawn cases.
 * @internal
 */
export async function spawnAsync<T extends Actor>(prefabOrClass: SpawnableType, ...args: [options?: SpawnOptions]): Promise<T> {
  const [options] = args;
  const { cls, prefabPath } = extractClsOrPrefabPath(prefabOrClass);

  if (cls) {
    return Promise.resolve(spawnByClass<T>(cls, options));
  } else if (prefabPath) {
    const { prefabData, ...instOptions } = options ?? {};
    return await instantiatePrefabAsync<T>(prefabPath, instOptions);
  }

  throw new Error(`Cannot spawn from ${prefabOrClass}`);
}
